import { useCallback } from 'react';
import { formatDays } from '@odo/shared-ui/clinical/formatDays';
import { RichEditor } from '@odo/shared-ui';
import type { ElasticScheme, LegacyElastics } from '@odo/shared-ui/clinical/elastics/model';
import { AttachmentRecap } from '@odo/shared-ui/clinical/recap/AttachmentRecap';
import { ElasticsRecap } from '@odo/shared-ui/clinical/recap/ElasticsRecap';
import { RecapPanel, RecapRow } from '@odo/shared-ui/clinical/recap/RecapPanel';
import type { ClinicalFormFields } from './ClinicalFormStep';
import type { TiptapDoc } from '@odo/shared-ui';
import type { ManufacturerPdfRef } from '@odo/shared-ui/clinical/types';
import {
    VirtualAppointmentRecapList,
    type VirtualAppointmentRecapListProps
} from '@odo/shared-ui/clinical/virtualAppointments/VirtualAppointmentRecapList';

interface ClinicalFieldRecapProps {
    fields: ClinicalFormFields;
    elasticsSchemes: ElasticScheme[];
    legacyElastics?: LegacyElastics;
    nextAppointment: string;
    notesDoc: TiptapDoc;
    /** Pre-VA free-text note; only shown when non-blank. */
    legacyVcaNote?: string;
    virtualAppointments: VirtualAppointmentRecapListProps;
    /** Raw File present when attachment was chosen in this session. */
    attachment?: File | null;
    /** Persisted ref used when resuming after close (no raw File available). */
    attachmentRef?: ManufacturerPdfRef | null;
}

const noopUpload: (file: File) => Promise<{ url: string; fileId: string }> = () =>
    Promise.resolve({ url: '', fileId: '' });

// Read-only recap of an SDI/RDI, shared by the wizard review step and read-only surfaces.
export function ClinicalFieldRecap({
    fields,
    elasticsSchemes,
    legacyElastics,
    nextAppointment,
    notesDoc,
    legacyVcaNote = '',
    virtualAppointments,
    attachment,
    attachmentRef
}: ClinicalFieldRecapProps) {
    const noopOnChange = useCallback(() => {}, []);

    const showAttachmentPanel = attachment !== undefined || attachmentRef !== undefined;
    const attachmentName = attachment?.name ?? attachmentRef?.filename ?? null;
    const attachmentSize = attachment?.size ?? attachmentRef?.sizeBytes ?? null;

    return (
        <div className="min-h-0 flex flex-col gap-3 overflow-auto">
            {showAttachmentPanel && (
                <RecapPanel title="Manufacturer PDF">
                    <AttachmentRecap name={attachmentName} sizeBytes={attachmentSize} />
                </RecapPanel>
            )}

            <RecapPanel title="Patient">
                <RecapRow label="Name" value={fields.patientName} />
                <RecapRow label="Date" value={fields.date} />
            </RecapPanel>

            <RecapPanel title="Elastics">
                <ElasticsRecap schemes={elasticsSchemes} legacy={legacyElastics} />
            </RecapPanel>

            <RecapPanel title="Aligner schedule">
                <RecapRow label="Aligners provided" value={fields.numberOfAligners} />
                <RecapRow label="Next appointment at aligner" value={nextAppointment} />
                <RecapRow label="DM scan frequency" value={formatDays(fields.dmScanFrequency)} />
                <RecapRow
                    label="Aligner change frequency"
                    value={formatDays(fields.alignerChangeFrequency)}
                />
                <RecapRow label="Aligner wear schedule" value={fields.alignerWear} />
            </RecapPanel>

            <RecapPanel title="Notes / Instructions">
                <RichEditor
                    value={notesDoc}
                    onChange={noopOnChange}
                    onImageUpload={noopUpload}
                    readOnly
                    chrome="flush"
                    minHeightPx={40}
                />
            </RecapPanel>

            <RecapPanel title="VAs">
                <VirtualAppointmentRecapList {...virtualAppointments} />
            </RecapPanel>

            {legacyVcaNote.trim() !== '' && (
                <RecapPanel title="Legacy note">
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">{legacyVcaNote}</div>
                </RecapPanel>
            )}
        </div>
    );
}
